import React, { useState, useEffect, lazy, Suspense } from "react";
import EN from "../data/en.json";
import AR from "../data/ar.json";
import { Footer } from "../components/footer";
import { Aboutus } from "../components/Aboutus";
import { EnterpriseNavbar } from "../components/enterprise/EnterpriseNavbar";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { mergeEnterpriseCaseStudyFarms } from "../data/enterpriseCaseStudyContent";

// Lazy-load below-fold heavy sections
const PlatformShowcase = lazy(() =>
  import("../components/enterprise/PlatformShowcase").then(m => ({ default: m.PlatformShowcase }))
);
const EnterpriseIntegratedModelSection = lazy(() =>
  import("../components/enterprise/EnterpriseIntegratedModelSection")
);
const EnterpriseInquiryForm = lazy(() =>
  import("../components/enterprise/EnterpriseInquiryForm").then(m => ({ default: m.EnterpriseInquiryForm }))
);

const SectionFallback = () => (
  <div className="flex items-center justify-center py-20">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-200 border-t-emerald-600" />
  </div>
);

const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const total = document.documentElement.scrollHeight - window.innerHeight;
        setProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return <div className="fixed top-0 left-0 z-[100] h-1 bg-emerald-500 transition-[width] duration-100" style={{ width: `${progress}%` }} />;
};

const FadeIn = ({ children, delay = 0, className = "", direction = "up" }) => {
  const [ref, inView] = useInView({ triggerOnce: true, rootMargin: "-50px 0px" });
  
  const yOffset = direction === "up" ? 30 : direction === "down" ? -30 : 0;
  const xOffset = direction === "left" ? 30 : direction === "right" ? -30 : 0;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: yOffset, x: xOffset }}
      animate={inView ? { opacity: 1, y: 0, x: 0 } : { opacity: 0, y: yOffset, x: xOffset }}
      transition={{ duration: 0.8, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const useCountUp = (end, duration = 2000, inView = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const isNumeric = /^[\d,]+/.test(end);
    if (!isNumeric) { setCount(end); return; }
    const target = parseInt(end.replace(/,/g, ""), 10);
    const startTime = performance.now();
    let rafId;
    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [end, duration, inView]);
  return count;
};

const MetricCircle = ({ value, unit, label, suffix = "", delay = 0, isArabic }) => {
  const [ref, inView] = useInView({ triggerOnce: true, rootMargin: "-40px 0px" });
  const isNumeric = /^[\d,]+/.test(value);
  const numericPart = isNumeric ? value.replace(/[^\d]/g, "") : null;
  const suffixPart = isNumeric ? value.replace(/[\d,]/g, "") : null;
  const animatedCount = useCountUp(numericPart || value, 2000, inView);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={inView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className="flex flex-col items-center text-center"
    >
      <div className="relative w-28 h-28 md:w-32 md:h-32 3xl:w-40 3xl:h-40 4k:w-48 4k:h-48 flex items-center justify-center">
        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="54" fill="none" stroke="#d1fae5" strokeWidth="4" />
          <motion.circle
            cx="60" cy="60" r="54"
            fill="none" stroke="#10b981" strokeWidth="4" strokeLinecap="round"
            strokeDasharray={339.3}
            initial={{ strokeDashoffset: 339.3 }}
            animate={inView ? { strokeDashoffset: 0 } : {}}
            transition={{ duration: 2, delay: delay + 0.2, ease: "easeOut" }}
            transform="rotate(-90 60 60)"
          />
        </svg>
        <div className="flex flex-col items-center leading-tight">
          <span className="text-2xl md:text-3xl 3xl:text-4xl 4k:text-5xl font-bold text-emerald-700">
            {isNumeric ? animatedCount.toLocaleString() : value}
            {suffixPart}
          </span>
          {unit && (
            <span className="text-xs md:text-sm 3xl:text-base 4k:text-lg font-semibold text-emerald-600 -mt-0.5">
              {unit}
            </span>
          )}
        </div>
      </div>
      <p className="mt-3 text-sm font-semibold text-slate-600 max-w-[160px] leading-snug">{label}</p>
    </motion.div>
  );
};

const SectionIntro = ({ eyebrow, title, paragraph, isArabic }) => (
  <FadeIn className={`${isArabic ? "text-right" : "text-left"}`}>
    {eyebrow ? (
      <p className="mb-3 text-sm font-semibold uppercase tracking-[0.24em] text-emerald-600">
        {eyebrow}
      </p>
    ) : null}
    <h2 className="mb-3 text-3xl font-semibold text-slate-900 md:text-5xl 3xl:text-6xl 4k:text-7xl">
      {title}
    </h2>
    {paragraph ? (
      <p className="mb-0 max-w-3xl 3xl:max-w-4xl text-base leading-relaxed text-slate-600 md:text-xl 3xl:text-2xl 4k:text-3xl">
        {paragraph}
      </p>
    ) : null}
  </FadeIn>
);

const LAYER_OPTIONS = [
  { key: "NATURAL_COLOR", label: "Natural", labelAr: "طبيعي" },
  { key: "NDVI", label: "NDVI", labelAr: "NDVI" },
  { key: "MOISTURE", label: "Moisture", labelAr: "رطوبة" },
];

/**
 * Base URLs for timelapse assets.
 * In development the CRA dev server proxies /timelapse-remote/* to GCS,
 * avoiding browser CORS restrictions. In production the full GCS URL is used.
 * Local public/timelapse is the ultimate fallback when GCS is unreachable.
 */
// Same-origin proxy → GCS (server.js in prod, setupProxy.js in dev). Fetches go
// through this so the manifest load has NO browser-CORS dependency on the bucket.
const TIMELAPSE_PROXY_BASE_URL = "/timelapse-remote";
const TIMELAPSE_REMOTE_BASE_URL =
  process.env.NODE_ENV === "development"
    ? TIMELAPSE_PROXY_BASE_URL
    : "https://storage.googleapis.com/farm-topia.appspot.com/timelapse";
const TIMELAPSE_LOCAL_BASE_URL = `${process.env.PUBLIC_URL || ""}/timelapse`;

/**
 * SatelliteTimelapse — VISUALIZATION PATH
 *
 * Loads pre-generated PNG frames from Cloud Storage (prod) or local public/ (dev).
 * Manifest at {base}/{farmId}/manifest.json is produced by
 * platform/scripts/generate-timelapse-frames.js (CREATION PATH).
 */
const SatelliteTimelapse = ({ farm, isArabic }) => {
  const farmId = farm?.id || null;
  const requestConfig = farm?.satelliteTimeseriesRequest || null;
  const summaryBadges = farm?.metrics?.slice(0, 2)?.length
    ? farm.metrics.slice(0, 2)
    : [
        farm?.size ? { label: isArabic ? "المساحة" : "Area", value: farm.size } : null,
        farm?.solar ? { label: isArabic ? "الطاقة" : "Energy", value: farm.solar } : null,
      ].filter(Boolean);

  const [manifest, setManifest] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | error
  const [error, setError] = useState("");
  const [activeLayer, setActiveLayer] = useState("NATURAL_COLOR");
  const [playing, setPlaying] = useState(false);
  const [currentDate, setCurrentDate] = useState("");
  const [progress, setProgress] = useState(0);

  const videoRef = React.useRef(null);

  const scenes = manifest?.scenes || [];
  const totalScenes = scenes.length;

  // Build video URL for active layer — always start from remote
  const videoUrl = farmId
    ? `${TIMELAPSE_REMOTE_BASE_URL}/${farmId}/${activeLayer}.mp4`
    : null;

  // 1. Load manifest on mount / farm change — remote-first with local fallback
  useEffect(() => {
    if (!farmId) return;
    setStatus("loading");
    setManifest(null);
    setPlaying(false);
    setCurrentDate("");
    setProgress(0);
    setError("");

    let cancelled = false;

    const fetchManifest = (baseUrl) =>
      fetch(`${baseUrl}/${farmId}/manifest.json`).then(async (r) => {
        if (!r.ok) throw new Error(`Manifest not found (HTTP ${r.status})`);
        // A catch-all/SPA host answers 200 with its HTML index page for a
        // missing asset, and a cross-origin GCS fetch can be CORS-blocked.
        // Read as text and validate before parsing so neither case surfaces as
        // a raw "JSON.parse: unexpected character" to visitors — an invalid
        // body just falls through to the next source (or the graceful state).
        const text = await r.text();
        if (text.trimStart().startsWith("<")) {
          throw new Error("Manifest unavailable (received HTML, not JSON)");
        }
        try {
          return JSON.parse(text);
        } catch {
          throw new Error("Manifest unavailable (invalid JSON)");
        }
      });

    // Remote direct (used if the bucket ever gets a CORS policy) → same-origin
    // proxy (no browser-CORS dependency — the reliable path) → bundled local
    // (usually absent; last resort).
    fetchManifest(TIMELAPSE_REMOTE_BASE_URL)
      .catch(() => fetchManifest(TIMELAPSE_PROXY_BASE_URL))
      .catch(() => fetchManifest(TIMELAPSE_LOCAL_BASE_URL))
      .then((data) => {
        if (cancelled) return;
        setManifest(data);
        setStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus("error");
        setError(err?.message || "Failed to load timelapse manifest");
      });

    return () => { cancelled = true; };
  }, [farmId]);

  // 2. Auto-play when video is ready or layer changes
  useEffect(() => {
    const video = videoRef.current;
    if (!video || status !== "ready") return;

    // Reset and play when source changes
    video.load();
    video.play().then(() => setPlaying(true)).catch(() => {});
  }, [activeLayer, status, videoUrl]);

  // Smooth progress via requestAnimationFrame (60 fps instead of ~4 fps timeupdate)
  const rafRef = React.useRef(null);
  const scenesRef = React.useRef(scenes);
  scenesRef.current = scenes;

  React.useEffect(() => {
    const tick = () => {
      const video = videoRef.current;
      const sc = scenesRef.current;
      if (video && sc.length && !video.paused) {
        const duration = video.duration || 1;
        const ratio = video.currentTime / duration;
        setProgress(ratio);
        const idx = Math.min(Math.floor(ratio * sc.length), sc.length - 1);
        setCurrentDate(sc[idx]?.date || "");
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const onVideoEnded = React.useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    video.play().catch(() => {});
  }, []);

  const formatDate = (value) => {
    if (!value) return "";
    const normalizedValue =
      typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? `${value}T12:00:00Z`
        : value;
    return new Intl.DateTimeFormat(isArabic ? "ar-EG" : "en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }).format(new Date(normalizedValue));
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  };

  const seekTo = (e) => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    video.currentTime = ratio * video.duration;
  };

  const isLoadingManifest = status === "loading";
  const hasVideo = status === "ready" && totalScenes > 0 && videoUrl;

  return (
    <div className="relative w-full overflow-hidden rounded-[2rem] bg-[#07131b] border border-[#17303d] shadow-2xl" style={{ height: 'clamp(420px, 56vw, 80vh)' }}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.22),_transparent_40%),linear-gradient(180deg,_rgba(4,18,24,0.92),_rgba(7,19,27,1))]" />

      {/* Video display */}
      {hasVideo && (
        <video
          ref={videoRef}
          key={videoUrl}
          src={videoUrl}
          muted
          playsInline
          onEnded={onVideoEnded}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onError={(e) => {
            const video = e.currentTarget;
            const remotePath = `${TIMELAPSE_REMOTE_BASE_URL}/${farmId}/`;
            const proxyPath = `${TIMELAPSE_PROXY_BASE_URL}/${farmId}/`;
            // Direct GCS video failed → retry through the same-origin proxy
            // (video playback itself is CORS-free, but this covers network/geo
            // blocks and keeps prod off any bucket-CORS dependency).
            if (remotePath !== proxyPath && video.src.startsWith(remotePath)) {
              const wasPlaying = !video.paused;
              video.src = video.src.replace(remotePath, proxyPath);
              video.load();
              if (wasPlaying) {
                video.play().catch(() => {});
              }
            }
          }}
          className="absolute inset-0 h-full w-full object-contain"
        />
      )}

      {/* Overlay gradient for readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#07131b] via-transparent to-[#07131bcc]" />

      <div className="relative z-10 flex h-full flex-col justify-between p-5 md:p-6">
        {/* Header */}
        <div className={`flex items-start justify-between gap-4 ${isArabic ? "flex-row-reverse text-right" : "text-left"}`}>
          <div>
            <div className={`mb-2 flex items-center gap-3 ${isArabic ? "flex-row-reverse" : ""}`}>
              <span className="relative flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
              </span>
              <span className="text-[11px] font-mono uppercase tracking-[0.3em] text-emerald-300">
                Sentinel-2 Timelapse
              </span>
            </div>
            <h4 className="text-xl font-bold text-white md:text-2xl drop-shadow-lg">
              {farm?.name || (isArabic ? "التصوير الزمني" : "Satellite Timelapse")}
            </h4>
            {farm?.description && (
              <p className="mt-1 max-w-md text-sm text-slate-300/90 leading-relaxed drop-shadow">
                {farm.description}
              </p>
            )}
            {(manifest || requestConfig) && (
              <p className="mt-1 text-xs text-slate-400 drop-shadow">
                {formatDate(manifest?.scenes?.[0]?.date || requestConfig?.startDate)} – {formatDate(manifest?.scenes?.[manifest.scenes.length - 1]?.date || requestConfig?.endDate)}
              </p>
            )}
            <div className={`mt-2 flex flex-wrap gap-1.5 ${isArabic ? "justify-end" : ""}`}>
              {farm?.crops && (
                <span className="px-2.5 py-0.5 bg-white/10 text-slate-200 rounded-md text-xs font-medium backdrop-blur-sm">
                  {farm.crops}
                </span>
              )}
              {summaryBadges.map((metric) => (
                <span key={metric.label} className="px-2.5 py-0.5 bg-emerald-500/15 text-emerald-200 rounded-md text-xs font-medium border border-emerald-500/20 backdrop-blur-sm">
                  {metric.label}: {metric.value}
                </span>
              ))}
            </div>
          </div>

          {/* Layer toggle */}
          <div className={`flex flex-wrap gap-1.5 ${isArabic ? "justify-start" : "justify-end"}`}>
            {LAYER_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setActiveLayer(opt.key)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                  activeLayer === opt.key
                    ? "border border-emerald-500/60 bg-emerald-500/20 text-emerald-200"
                    : "border border-slate-600/50 bg-slate-900/60 text-slate-300 hover:bg-slate-800/60"
                }`}
              >
                {isArabic ? opt.labelAr : opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Center area: loading states */}
        <div className="relative flex-1 flex items-center justify-center">
          {isLoadingManifest && (
            <div className="flex flex-col items-center gap-3">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-200/20 border-t-emerald-400" />
              <span className="text-sm text-slate-300">{isArabic ? "جاري التحميل…" : "Loading…"}</span>
            </div>
          )}

          {status === "error" && (
            <div
              title={error || undefined}
              className="rounded-[1.5rem] border border-rose-500/30 bg-rose-500/10 px-6 py-4 text-center text-sm text-rose-100"
            >
              {isArabic
                ? "معاينة الأقمار الصناعية غير متوفرة حالياً."
                : "Live satellite preview is temporarily unavailable."}
            </div>
          )}

          {status === "ready" && !scenes.length && (
            <div className="rounded-[1.5rem] border border-slate-700 bg-slate-900/60 px-6 py-4 text-center text-sm text-slate-200">
              {isArabic
                ? "لم يتم العثور على مشاهد لهذا النطاق الزمني."
                : "No satellite scenes found for this date range."}
            </div>
          )}
        </div>

        {/* Bottom controls */}
        <div className="space-y-3">
          {/* Date overlay */}
          {hasVideo && (
            <div className={`flex items-center justify-between ${isArabic ? "flex-row-reverse" : ""}`}>
              <span className="rounded-lg bg-black/60 px-3 py-1 text-sm font-semibold text-white backdrop-blur-sm">
                {currentDate ? formatDate(currentDate) : ""}
              </span>
              <span className="text-xs text-slate-400">
                {totalScenes} {isArabic ? "مشهد" : "scenes"}
              </span>
            </div>
          )}

          {/* Seek bar */}
          {hasVideo && (
            <div
              className="group relative h-1.5 w-full cursor-pointer rounded-full bg-slate-700"
              onClick={seekTo}
            >
              <div
                className="h-full rounded-full bg-emerald-500"
                style={{ width: `${progress * 100}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 h-3.5 w-3.5 rounded-full bg-emerald-400 border-2 border-white shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ left: `calc(${progress * 100}% - 7px)` }}
              />
            </div>
          )}

          {/* Play/pause + metrics row */}
          <div className={`flex items-center gap-3 ${isArabic ? "flex-row-reverse" : ""}`}>
            {hasVideo && (
              <button
                onClick={togglePlay}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 transition-colors"
                aria-label={playing ? "Pause" : "Play"}
              >
                {playing ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <rect x="3" y="2" width="4" height="12" rx="1" />
                    <rect x="9" y="2" width="4" height="12" rx="1" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M4 2.5v11l9-5.5z" />
                  </svg>
                )}
              </button>
            )}

            <div className={`grid flex-1 grid-cols-3 gap-3 ${isArabic ? "text-right" : "text-left"}`}>
              <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-sm">
                <div className="text-[11px] uppercase tracking-[0.24em] text-slate-400">
                  {isArabic ? "المشاهد" : "Scenes"}
                </div>
                <div className="mt-2 text-xl font-semibold text-white">
                  {totalScenes || "—"}
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-sm">
                <div className="text-[11px] uppercase tracking-[0.24em] text-slate-400">
                  {isArabic ? "الطبقة" : "Layer"}
                </div>
                <div className="mt-2 text-xl font-semibold text-white">
                  {LAYER_OPTIONS.find((o) => o.key === activeLayer)?.[isArabic ? "labelAr" : "label"] || activeLayer}
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-sm">
                <div className="text-[11px] uppercase tracking-[0.24em] text-slate-400">
                  {isArabic ? "الجودة" : "Quality"}
                </div>
                <div className="mt-2 text-xl font-semibold text-white">
                  HD
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const EnterprisesPage = ({ lang, toggleLanguage }) => {
  const isArabic = lang === "AR";
  const data = isArabic ? AR : EN;
  const enterprise = data.Enterprise;
  const caseStudyUi = enterprise.caseStudies?.ui || {};
  const navItems = [
    { label: isArabic ? "نظرة عامة" : "Overview", url: "#enterprise-hero" },
    { label: isArabic ? "المنصة" : "Platform", url: "#platform" },
    { label: isArabic ? "سجل الإنجاز" : "Track Record", url: "#track-record" },
    { label: isArabic ? "النموذج" : "Model", url: "#operating-model" },
    { label: isArabic ? "المحاصيل" : "Crops", url: "/crops" },
    // Platform usage guide (the Figma walkthrough) is now ported into this
    // landing CRA at /guide and /guide/:slug — see src/pages/Guide.jsx.
    { label: isArabic ? "الدليل" : "Guide", url: "/guide" },
    { label: isArabic ? "تواصل" : "Inquiry", url: "#inquiry" },
  ];
  const caseStudyFarms = mergeEnterpriseCaseStudyFarms(enterprise.caseStudies.farms);
  const provenMetrics = [
    {
      value: isArabic ? "3000+" : "3,000+",
      unit: isArabic ? "فدان" : "Acre",
      label: isArabic ? "فدان بين تطوير وتشغيل" : "Acres across development and operations",
    },
    {
      value: isArabic ? "1+" : "1+",
      unit: isArabic ? "ميجاوات" : "MW",
      label: isArabic ? "قدرة طاقة شمسية مرتبطة بمنظومات الري" : "Solar capacity tied to irrigation systems",
    },
    {
      value: isArabic ? "50+" : "50+",
      unit: isArabic ? "كم" : "KM",
      label: isArabic ? "شبكات ري متصلة بالطاقة الشمسية" : "Irrigation networks powered by solar energy",
    },
    {
      value: isArabic ? "1,200+" : "1,200+",
      unit: isArabic ? "طن" : "Ton",
      label: isArabic ? "طن من الإنتاج الزراعي" : "Tons of crop yield produced",
    },
    {
      value: isArabic ? "1,000+" : "1,000+",
      unit: isArabic ? "مستخدم" : "Users",
      label: isArabic ? "مستخدم على المنصة" : "Platform users and growing",
    },
  ];
  const heroSecondaryCta = isArabic ? "استكشف منصتنا" : "Explore Our Platform";
  
  const [activeFarmIndex, setActiveFarmIndex] = useState(0);
  const [activeChipIndex, setActiveChipIndex] = useState(0);

  return (
    <div dir={isArabic ? "rtl" : "ltr"} className="min-h-screen bg-[#f5faf5] text-slate-900 font-sans selection:bg-emerald-200">
      <EnterpriseNavbar
        lang={lang}
        toggleLanguage={toggleLanguage}
        navItems={navItems}
        actions={enterprise.navCtas}
      />

      <ScrollProgress />

      <main>
        {/* HERO SECTION with Video Background */}
        <section
          id="enterprise-hero"
          className="relative flex items-center min-h-[90vh] overflow-hidden"
        >
          {/* Video Background */}
          <div className="absolute inset-0 z-0 bg-black">
            <video
              className="absolute inset-0 w-full h-full object-cover"
              src="https://firebasestorage.googleapis.com/v0/b/farm-topia.appspot.com/o/LandingPage%2FHero%20Section%20caption%20animation.mp4?alt=media&token=29156b8a-fcf0-49e1-88ab-ac4c878b95cb"
              poster="/img/Light/hero.png"
              preload="metadata"
              autoPlay
              loop
              muted
              playsInline
            />
            {/* Subtle bottom fade so hero blends into the next section */}
            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#f5faf5] to-transparent" />
          </div>

          <div className="relative z-10 mx-auto w-full max-w-7xl 3xl:max-w-[1600px] 4k:max-w-[2000px] px-4 py-14 md:px-6 md:py-20 3xl:px-10 3xl:py-28 4k:px-16 4k:py-36">
            <FadeIn className={`max-w-4xl 3xl:max-w-5xl 4k:max-w-6xl rounded-2xl bg-white/70 backdrop-blur-md p-8 md:p-12 3xl:p-16 4k:p-20 shadow-lg ${isArabic ? "text-right" : "text-left"}`}>
                <p className="mb-6 text-sm font-semibold tracking-wide text-emerald-700 uppercase">
                  {enterprise.hero.eyebrow}
                </p>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeChipIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                  >
                    <h1 className="max-w-4xl text-5xl font-bold leading-[1.1] text-slate-900 md:text-7xl 3xl:text-8xl 4k:text-9xl">
                      {enterprise.hero.chipContent[activeChipIndex].headline}
                    </h1>
                    <p className="mt-6 max-w-2xl 3xl:max-w-3xl text-xl leading-relaxed text-slate-700 font-medium 3xl:text-2xl 4k:text-3xl">
                      {enterprise.hero.chipContent[activeChipIndex].paragraph}
                    </p>
                  </motion.div>
                </AnimatePresence>

                <div className={`mt-10 flex flex-wrap gap-4 ${isArabic ? "justify-start" : ""}`}>
                  <a
                    href="#inquiry"
                    className="rounded-full bg-emerald-600 px-8 py-4 text-base font-semibold text-white no-underline transition-all hover:bg-emerald-700 hover:shadow-lg hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
                  >
                    {enterprise.hero.primaryCta}
                  </a>
                  <Link
                    to="/platform"
                    className="rounded-full border-2 border-emerald-600 px-8 py-4 text-base font-semibold text-emerald-700 no-underline transition-all hover:bg-emerald-50 hover:text-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
                  >
                    {heroSecondaryCta}
                  </Link>
                </div>

                <div className="mt-12 flex flex-wrap gap-3">
                  {enterprise.hero.chips.map((chip, i) => (
                    <motion.button
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.5 + (i * 0.1) }}
                      key={chip}
                      onClick={() => setActiveChipIndex(i)}
                      className={`rounded-full backdrop-blur px-4 py-2 text-sm font-semibold shadow-sm border transition-all cursor-pointer ${
                        i === activeChipIndex
                          ? "bg-emerald-600 text-white border-emerald-600 shadow-md"
                          : "bg-white/60 text-slate-800 border-white/50 hover:bg-white/80"
                      }`}
                    >
                      {chip}
                    </motion.button>
                  ))}
                </div>
            </FadeIn>
          </div>
        </section>

        <Suspense fallback={<SectionFallback />}>
          <PlatformShowcase isArabic={isArabic} />
        </Suspense>

        <Suspense fallback={<SectionFallback />}>
          <EnterpriseIntegratedModelSection isArabic={isArabic} />
        </Suspense>

        {/* CASE STUDIES SECTION */}
        {enterprise.caseStudies && (
          <section id="track-record" className="mx-auto max-w-7xl 3xl:max-w-[1600px] 4k:max-w-[2000px] px-4 py-20 md:px-6 md:py-28 3xl:px-10 3xl:py-36 4k:px-16 4k:py-44">
            <SectionIntro
              eyebrow={caseStudyUi.trackRecordEyebrow || (isArabic ? "سجل الإنجازات" : "Proven Track Record")}
              title={enterprise.caseStudies.title}
              paragraph={enterprise.caseStudies.paragraph}
              isArabic={isArabic}
            />

            <div className="mt-12 rounded-[2.5rem] border border-emerald-100 bg-white p-6 shadow-[0_24px_90px_rgba(15,23,42,0.08)] md:p-8">
              {/* Proven metrics inside case-study card */}
              <div className={`flex flex-wrap justify-center gap-6 md:gap-10 py-6 border-b border-emerald-100 ${isArabic ? "flex-row-reverse" : ""}`}>
                {provenMetrics.map((metric, i) => (
                  <MetricCircle key={metric.value} value={metric.value} unit={metric.unit} label={metric.label} delay={i * 0.12} isArabic={isArabic} />
                ))}
              </div>
              <div className="mt-10 grid gap-8 lg:grid-cols-[0.95fr_1.45fr] lg:items-center 4k:gap-12">
                <FadeIn direction={isArabic ? "right" : "left"} className="flex flex-col gap-3">
                  {caseStudyFarms.map((farm, index) => {
                    const isActive = activeFarmIndex === index;
                    return (
                      <button
                        key={farm.id}
                        onClick={() => setActiveFarmIndex(index)}
                        className={`${isArabic ? "text-right" : "text-left"} p-5 rounded-2xl border-2 transition-all duration-300 ${
                          isActive
                            ? "border-emerald-500 bg-emerald-50 shadow-md transform scale-[1.02]"
                            : "border-transparent bg-slate-50 hover:bg-white hover:border-emerald-100"
                        }`}
                      >
                        <h4 className={`text-xl font-bold mb-1 ${isActive ? "text-emerald-900" : "text-slate-800"}`}>
                          {farm.name}
                        </h4>
                        <p className="text-sm font-medium text-slate-500 mb-2 flex items-center gap-2">
                          <span>{farm.size}</span>
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                          <span>{farm.solar}</span>
                        </p>
                        <p className="mb-0 text-sm font-medium leading-relaxed text-slate-600">{farm.description}</p>
                      </button>
                    );
                  })}
                </FadeIn>

                <FadeIn direction={isArabic ? "left" : "right"} className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-tr from-emerald-100 to-transparent opacity-50 rounded-[3rem] blur-xl" />
                  <div className="relative bg-white rounded-[2.5rem] border border-emerald-100 shadow-xl overflow-hidden flex flex-col gap-6 3xl:gap-8">
                    <SatelliteTimelapse farm={caseStudyFarms[activeFarmIndex]} isArabic={isArabic} />

                    <div className="px-6 pb-6 md:px-8 md:pb-8 3xl:px-12 3xl:pb-12">
                      <Link
                        to={`/projects/${caseStudyFarms[activeFarmIndex].id}`}
                        className="inline-flex items-center gap-2 self-start rounded-full bg-slate-900 px-6 py-3 text-sm font-bold text-white no-underline hover:bg-slate-800 transition-colors shadow-lg"
                      >
                        {enterprise.caseStudies.viewCaseStudy || (isArabic ? "عرض دراسة الحالة الكاملة" : "View Full Case Study")} →
                      </Link>
                    </div>
                  </div>
                </FadeIn>
              </div>
            </div>
          </section>
        )}

        {/* INQUIRY FORM */}
        <div className="mx-auto max-w-7xl 3xl:max-w-[1600px] 4k:max-w-[2000px] px-4 py-20 md:px-6 md:py-28 3xl:px-10 3xl:py-36 4k:px-16 4k:py-44 relative z-20 bg-[#f5faf5]">
          <FadeIn direction="up">
            <Suspense fallback={<SectionFallback />}>
              <EnterpriseInquiryForm lang={lang} content={enterprise.inquiry} />
            </Suspense>
          </FadeIn>
        </div>

        <section className="pb-20 md:pb-28">
          <Aboutus lang={lang} />
        </section>
      </main>

      <Footer lang={lang} />
    </div>
  );
};

export default EnterprisesPage;
